import React, { useState, useEffect } from 'react';
import { 
  Layers, Plus, X, Search, Edit2, 
  Archive, CheckCircle, AlertTriangle, Trash2, FolderTree 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const CategorieMp = () => {
  const [categories, setCategories] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('actives'); // 'actives' ou 'archives'
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    nom: ''
  });

  const [alert, setAlert] = useState({
    show: false,
    type: 'info', 
    message: '',
    onConfirm: null,
    color: '#6366f1'
  });

  const genererIdCat = () => `CAT-MP-${Date.now().toString().slice(-6)}`;

  useEffect(() => {
    fetchCategories();

    const handleGlobalUpdate = (event) => {
      const { table } = event.detail;
      if (['categories_mp', 'all'].includes(table)) {
        fetchCategories();
      }
    };

    window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
  }, []);

  // --- SYNCHRONISATION TEMPS RÉEL (SOCKET) ---
  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      console.log("🔄 Mise à jour des catégories MP via Socket");
      fetchCategories(); 
    };

    socket.on('REFRESH_UI', (data) => {
      if (data?.module === 'CATEGORIES_MP' || data?.module === 'RAW_MATERIALS') {
        handleUpdate();
      }
    });

    return () => {
      socket.off('REFRESH_UI');
    };
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await API.get('/categories-mp').catch(() => API.get('/raw-materials-categories').catch(() => ({ data: [] })));
      const rawData = Array.isArray(res.data) ? res.data : (res.data?.categories || res.data?.data || []);
      setCategories(Array.isArray(rawData) ? rawData : []);
    } catch (err) {
      showAlert('error', 'Impossible de charger les catégories.', '#ef4444');
    }
  };

  const showAlert = (type, message, color = '#6366f1', onConfirm = null) => {
    setAlert({ show: true, type, message, onConfirm, color });
    if (type !== 'confirm') {
      setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 4000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) return;

    const payload = {
      nom: formData.nom.toUpperCase(),
      is_active: 1
    };

    try {
      if (editingId) {
        await API.put(`/categories-mp/${editingId}`, payload).catch(() => API.put(`/raw-materials-categories/${editingId}`, payload));
        showAlert('success', 'Catégorie mise à jour avec succès !', '#6366f1');
      } else {
        await API.post('/categories-mp', { id: genererIdCat(), ...payload }).catch(() => API.post('/raw-materials-categories', { id: genererIdCat(), ...payload }));
        showAlert('success', 'Catégorie créée avec succès !', '#6366f1');
      }

      setEditingId(null);
      setFormData({ nom: '' });
      setIsFormOpen(false);
      fetchCategories();
    } catch (err) {
      showAlert('error', "Échec de l'enregistrement de la catégorie.", '#ef4444');
    }
  };

  const prepareEdit = (cat) => {
    setEditingId(cat.id || cat.ID);
    setFormData({ nom: cat.nom || cat.NOM });
    setIsFormOpen(true);
  };

  const toggleArchive = (cat) => {
    const isArchiving = (cat.is_active ?? 1) === 1;
    const actionLabel = isArchiving ? 'Archiver' : 'Restaurer';
    const nextStatus = isArchiving ? 0 : 1;

    showAlert(
      'confirm',
      `${actionLabel} la catégorie "${cat.nom || cat.NOM}" ?`,
      isArchiving ? '#e11d48' : '#6366f1',
      async () => {
        try {
          await API.put(`/categories-mp/${cat.id || cat.ID}`, { ...cat, is_active: nextStatus })
            .catch(() => API.put(`/raw-materials-categories/${cat.id || cat.ID}`, { ...cat, is_active: nextStatus }));
          fetchCategories();
          showAlert('success', `Catégorie ${actionLabel.toLowerCase()}e avec succès.`, '#6366f1');
        } catch (err) {
          showAlert('error', "Erreur lors du changement de statut.", '#ef4444');
        }
      }
    );
  };

  const handleDelete = (catId, catNom) => {
    showAlert(
      'confirm',
      `Supprimer définitivement la catégorie "${catNom}" ?`,
      '#e11d48',
      async () => {
        try {
          await API.delete(`/categories-mp/${catId}`).catch(() => API.delete(`/raw-materials-categories/${catId}`));
          fetchCategories();
          showAlert('success', 'Catégorie supprimée.', '#6366f1');
        } catch (err) {
          showAlert('error', "Impossible de supprimer (vérifiez si des matières premières y sont rattachées).", '#ef4444');
        }
      }
    );
  };

  // Couleurs Indigo / Thème distinct
  const primaryColor = '#6366f1';
  const lightBgColor = '#e0e7ff';
  const borderColor = '#e2e8f0';

  const s = {
    layout: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#f8fafc' },
    mainArea: { flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative' },
    topSection: { flexShrink: 0, zIndex: 10, background: 'white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
    scrollableContent: { flex: 1, overflowY: 'auto', padding: '30px 40px' },
    alertContainer: {
        position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, display: 'flex', alignItems: 'center', gap: '15px',
        padding: '12px 24px', borderRadius: '12px', background: 'white',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)', borderLeft: `5px solid ${alert.color}`,
        minWidth: '350px', transition: 'all 0.3s ease'
    },
    formCard: {
      background: '#f8fafc',
      border: `1px solid ${borderColor}`,
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '30px',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
    },
    input: { padding: '12px 16px', borderRadius: '10px', border: `1px solid ${borderColor}`, fontSize: '14px', width: '100%', outline: 'none', background: 'white' },
    tab: (active) => ({
      padding: '14px 24px', cursor: 'pointer',
      borderBottom: active ? `3px solid ${primaryColor}` : '3px solid transparent',
      color: active ? primaryColor : '#64748b', fontWeight: active ? '800' : '600',
      display: 'flex', alignItems: 'center', gap: '10px', transition: '0.2s', fontSize: '13px'
    }),
    btnAction: {
      padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: '0.2s'
    }
  };

  return (
    <div style={s.layout}>
      <Sidebar />

      {alert.show && (
        <div style={s.alertContainer}>
            <div style={{ color: alert.color }}>
                {alert.type === 'confirm' ? <AlertTriangle size={24}/> : (alert.type === 'error' ? <X size={24}/> : <CheckCircle size={24}/>)}
            </div>
            <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{alert.message}</p>
            </div>
            {alert.type === 'confirm' ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setAlert({ ...alert, show: false })} style={{ padding: '5px 10px', border: 'none', background: '#f1f5f9', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>NON</button>
                    <button onClick={() => { alert.onConfirm(); setAlert({...alert, show: false}) }} style={{ padding: '5px 10px', border: 'none', background: alert.color, color: 'white', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>OUI</button>
                </div>
            ) : (
                <X size={18} style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setAlert({ ...alert, show: false })} />
            )}
        </div>
      )}

      <main style={s.mainArea}>
        <div style={s.topSection}>
            <header style={{ padding: '25px 40px', borderBottom: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ background: lightBgColor, color: primaryColor, padding: '12px', borderRadius: '12px', display: 'flex' }}>
                    <FolderTree size={26} />
                  </div>
                  <div>
                      <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '900', color: '#0f172a' }}>
                        Gestion des <span style={{color: primaryColor}}>Catégories MP</span>
                      </h1>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                        Structurez et organisez le classement de vos matières premières analytiques.
                      </p>
                  </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <Search style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <input 
                        type="text" placeholder="Rechercher..." 
                        style={{ padding: '10px 16px 10px 42px', borderRadius: '10px', border: `1px solid ${borderColor}`, width: '260px', outline: 'none', fontSize: '13px' }} 
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                  </div>
                  
                  <button 
                    onClick={() => {
                        if(isFormOpen) { setEditingId(null); setFormData({ nom: '' }); }
                        setIsFormOpen(!isFormOpen);
                    }} 
                    style={{ 
                        background: isFormOpen ? '#fee2e2' : primaryColor, 
                        color: isFormOpen ? '#ef4444' : 'white',
                        padding: '11px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', boxShadow: isFormOpen ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.25)'
                    }}
                  >
                    {isFormOpen ? <X size={18} /> : <Plus size={18} />}
                    {isFormOpen ? 'Fermer' : 'Nouvelle Catégorie'}
                  </button>
              </div>
            </header>
        </div>

        <div style={s.scrollableContent}>
          {/* Formulaire escamotable intégré élégamment */}
          {isFormOpen && (
            <div style={s.formCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: editingId ? '#3b82f6' : '#1e293b', textTransform: 'uppercase' }}>
                  {editingId ? `Modifier la catégorie` : "Ajouter une nouvelle catégorie"}
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>* Champs obligatoires</span>
              </div>
              <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '15px', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '6px' }}>NOM DE LA CATÉGORIE</label>
                    <input required style={s.input} placeholder="Ex: PRODUITS LAITIERS, EMBALLAGES..." value={formData.nom} onChange={e => setFormData({ nom: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {editingId && (
                    <button type="button" onClick={() => { setEditingId(null); setFormData({ nom: '' }); setIsFormOpen(false); }} style={{ background: '#e2e8f0', color: '#475569', border: 'none', padding: '12px 20px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '13px' }}>
                      Annuler
                    </button>
                  )}
                  <button type="submit" style={{ background: editingId ? '#3b82f6' : primaryColor, color: 'white', border: 'none', padding: '12px 25px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', fontSize: '13px', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)' }}>
                      {editingId ? 'Mettre à jour' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Onglets */}
          <div style={{ display: 'flex', background: 'white', borderRadius: '12px 12px 0 0', border: `1px solid ${borderColor}`, borderBottom: 'none', width: 'fit-content' }}>
            <div style={s.tab(activeTab === 'actives')} onClick={() => setActiveTab('actives')}>
              <Layers size={16} /> Actives ({categories.filter(c => (c.is_active ?? 1) === 1).length})
            </div>
            <div style={s.tab(activeTab === 'archives')} onClick={() => setActiveTab('archives')}>
              <Archive size={16} /> Archivées ({categories.filter(c => (c.is_active ?? 1) === 0).length})
            </div>
          </div>

          {/* Tableau */}
          <div style={{ background: 'white', borderRadius: '0 12px 12px 12px', border: `1px solid ${borderColor}`, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', marginBottom: '40px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>
                  <th style={{ padding: '15px 20px', color: '#475569', fontSize: '11px', fontWeight: '800', width: '180px' }}>IDENTIFIANT</th>
                  <th style={{ padding: '15px 20px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>DESIGNATION DE LA CATEGORIE</th>
                  <th style={{ padding: '15px 20px', textAlign: 'right', color: '#475569', fontSize: '11px', fontWeight: '800', width: '200px' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {categories
                  .filter(cat => (activeTab === 'actives' ? (cat.is_active ?? 1) === 1 : (cat.is_active ?? 1) === 0))
                  .filter(cat => (cat.nom || cat.NOM || '').toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(cat => {
                    const catId = cat.id || cat.ID;
                    const catNom = cat.nom || cat.NOM;
                    const isActive = (cat.is_active ?? 1) === 1;

                    return (
                      <tr key={catId} style={{ borderBottom: `1px solid ${borderColor}`, transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '16px 20px', fontSize: '12px', color: primaryColor, fontWeight: '700', fontFamily: 'monospace' }}>
                          {catId}
                        </td>
                        <td style={{ padding: '16px 20px', fontWeight: '700', color: '#0f172a', fontSize: '14px' }}>
                          {catNom}
                        </td>
                        <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {isActive ? (
                              <>
                                <button onClick={() => prepareEdit(cat)} style={{ ...s.btnAction, background: '#eff6ff', color: '#3b82f6' }} title="Modifier">
                                  <Edit2 size={16} />
                                </button>
                                <button onClick={() => toggleArchive(cat)} style={{ ...s.btnAction, background: '#fff1f2', color: '#e11d48' }} title="Archiver">
                                  <Archive size={16} />
                                </button>
                                <button onClick={() => handleDelete(catId, catNom)} style={{ ...s.btnAction, background: '#fef2f2', color: '#dc2626' }} title="Supprimer">
                                  <Trash2 size={16} />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => toggleArchive(cat)} style={{ ...s.btnAction, background: '#f0fdf4', color: '#16a34a', gap: '5px', fontSize: '12px', fontWeight: 'bold', padding: '6px 14px' }}>
                                <CheckCircle size={15} /> Restaurer
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CategorieMp;